/**
 * Which recommended photos belong to the Fire Life Safety block, and what each
 * one has to show (spec Amendment 5, D-024).
 *
 * A constants module rather than part of `FlsBlock.tsx`: TWO callers need it
 * and `FlsBlock` is only a header now (D-028). `QuestionList` reads
 * `isFlsQuestion` to find where that header goes; `photoShelf.ts` reads
 * `FLS_SHOT_NOTES` to caption the FLS shots among the rows `PhotoShelf`
 * renders. Neither caller should have to import `FlsBlock.tsx` just to get a
 * shot id list.
 *
 * ── One row per shot per screen ──────────────────────────────────────────────
 * `PhotoShelf` is now the only surface that turns a shot into a row (D-033
 * R16): the section close panel that used to carry its own leaving-the-room
 * checklist is deleted, so there is no second list left to duplicate against.
 * `FLS_SHOT_NOTES` only supplies the caption `photoShelf.ts` puts on an FLS
 * card in that one shelf — it renders no row of its own. Two identical "Fire
 * service relays · Not taken yet" rows on one 430px screen was the failure
 * mode this constant was built to avoid; it cannot recur now that the shelf
 * is the only place a shot is ever offered.
 */

import { shotBaseId } from '../../core/sections';

/**
 * The §3.11 id scheme, read as the catalog states it: one prefix **F**, then a
 * number, then the usual lowercase suffix for a follow-up (`F11a`). Matching on
 * the documented convention rather than listing fifteen ids is what keeps a
 * sixteenth FLS question from arriving in the catalog and rendering outside its
 * own block with nobody noticing.
 *
 * `tests/unit/fls-ui.test.ts` pins it against the catalog in both directions:
 * every `F…` question matches, and no other question does.
 */
const FLS_QUESTION_ID = /^F\d/;

export function isFlsQuestion(questionId: string): boolean {
  return FLS_QUESTION_ID.test(questionId);
}

/**
 * The recommended shots the FLS block carries, and what each picture has to
 * show. Keyed by the shot ids declared in `SHOT_LISTS` (src/core/sections.ts).
 *
 * The sticker sentence is the load-bearing one. Chip's interview was explicit
 * that the servicing company and its phone number are read off a sticker on the
 * panel, and that the FLS company works from that picture — so the row says so
 * in those words rather than leaving the surveyor to rank two photos of the same
 * box by guesswork.
 *
 * A shot id that no longer exists in the catalog would silently stop being
 * offered at all — the shelf is the one surface now that the close panel's
 * `ShotList` is gone (D-033 R16) — so `tests/unit/fls-ui.test.ts` asserts
 * every key is a real shot, and that every section carrying one also asks an
 * ungated FLS question — which is what guarantees the `FlsBlock` header
 * explaining those pictures is actually on the screen beside them.
 */
export const FLS_SHOT_NOTES: Readonly<Record<string, string>> = {
  // Amendment 6 (2026-08-18): the panel shots live on the Fire Life Safety
  // section now, and their ids follow it. `SHOT_LISTS.fls` still knows the
  // address they had on the building section, so an older record's pictures
  // count — but new captures are filed here.
  'fls.facp': 'The whole panel, close enough to read the make and model off the front.',
  'fls.facp_sticker':
    'This is the picture the fire alarm company actually works from. Get in close enough ' +
    'to read the company name and the phone number.',
  'electrical.fls_relays':
    'Whatever relays you can see in this room — one shot is fine if they sit together.',
  // Amendment 8 (D-041): keyed by BASE id — `isFlsShot`/`photoShelf.ts` strip a
  // per-car shot's `:carId` suffix before this lookup, so one caption covers
  // every car's row.
  'fls.facp_nameplate': 'Just the label with the make and model on it. Their report stalled for want of this one.',
  'general.controller': 'For the fire alarm company. The nameplate is the make and model — nobody retypes it.',
  'general.controller_inside': 'For the fire alarm company. Open the cabinet and shoot the boards and relays.',
  'cab.cop': 'The fire key switch and the floor buttons. If there are two panels, shoot both and say which is live.',
  'cab.certificate': 'The posted certificate — car number and capacity. Settles which car this is.',
  // `lobby.detector_egress` ("Main Egress Lobby") carries no caption since
  // D-046 (Chip, 2026-09-11): "the detector outside the Main Egress door" read
  // as the building's front door, not the elevator entrance the card is for.
  // The key stays so the shot is still one of the FLS company's (`isFlsShot`);
  // an empty caption prints nothing (photoShelf.ts).
  'lobby.detector_egress': '',
  'lobby.detector_typical': 'One typical floor. Add a few more floors as extras.',
  'pit.full_pit': 'From above. Show any detector or sprinkler head down there.',
  'electrical.disconnects': 'Every disconnect, and the breaker that feeds it.',
};

/**
 * True when this shot is one of the FLS shots — the ones `photoShelf.ts`
 * captions with `FLS_SHOT_NOTES` on the shelf card. Neither this file nor
 * `FlsBlock` renders a row for it; the close panel that used to divide shot
 * rows between itself and the block is gone (D-033 R16) — the shelf is the
 * only surface that ever was.
 */
export function isFlsShot(shotListId: string): boolean {
  return shotBaseId(shotListId) in FLS_SHOT_NOTES;
}

